import { useState, useEffect } from "react";
import { getAllEstudiantes } from "../../../services/estudiante.service";
import { getAllIdiomasByIdEstudiante } from "../../../services/idioma.service";
import { getAllRegistrosByIdEstudianteIdIdiomaEstado } from "../../../services/registro.service";
import { useSelector } from "react-redux";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { Globe, GraduationCap, Award, Trophy, Target, TrendingUp } from "lucide-react";
import StatsCard from "../../../components/animations/StatsCard";
import SubtleFloatingParticles from "../../../components/animations/SubtleFloatingParticles";

export const MisLogrosPage = () => {
  const [idiomasEstudiante, setIdiomasEstudiante] = useState(null);
  const [modulosCompletados, setModulosCompletados] = useState(0);
  const [modulosEnProgreso, setModulosEnProgreso] = useState(0);
  const [loading, setLoading] = useState(true);
  const userName = useSelector((store) => store.usuario.username);

  useEffect(() => {
    const obtenerDatos = async () => {
      try {
        setLoading(true);
        const userInfo = JSON.parse(localStorage.getItem("userInfoES") || "{}");
        const cacheKey = `estudianteIdByUser_${userName || userInfo?.username || ""}`;
        let estudianteId = JSON.parse(localStorage.getItem(cacheKey) || "null");

        let estudiante = null;
        if (!estudianteId) {
          const estudiantesData = await getAllEstudiantes();
          estudiante = estudiantesData.find((est) => est.usuario.username === userName);
          if (estudiante?.estudiante?.idEstudiante) {
            estudianteId = estudiante.estudiante.idEstudiante;
            localStorage.setItem(cacheKey, JSON.stringify(estudianteId));
          } else {
            console.log("Estudiante no encontrado");
            setLoading(false);
            return;
          }
        }

        const idiomas = await getAllIdiomasByIdEstudiante(estudianteId);
        setIdiomasEstudiante(idiomas);
        if (idiomas && idiomas.length > 0) {
          let totalCompletados = 0;
          let totalProgreso = 0;

          for (const idioma of idiomas) {
            const estadosCompletados = ["Aprobado", "Culminado"];
            for (const estado of estadosCompletados) {
              try {
                const registros = await getAllRegistrosByIdEstudianteIdIdiomaEstado(
                  estudianteId,
                  idioma.idIdioma,
                  estado
                );
                if (registros && Array.isArray(registros)) {
                  console.log(`✅ ${estado} en ${idioma.nombre}:`, registros);
                  totalCompletados += registros.length;
                }
              } catch (err) {
                if (err?.response?.status !== 404) {
                  console.log(`Error obteniendo ${estado}:`, err);
                }
              }
            }

            try {
              const progreso = await getAllRegistrosByIdEstudianteIdIdiomaEstado(
                estudianteId,
                idioma.idIdioma,
                "Progreso"
              );
              if (progreso && Array.isArray(progreso)) {
                totalProgreso += progreso.length;
              }
            } catch (err) {
              if (err?.response?.status !== 404) {
                console.log("Error obteniendo progreso:", err);
              }
            }
          }

          console.log("📊 Total módulos completados:", totalCompletados);
          console.log("📚 Total módulos en progreso:", totalProgreso);
          setModulosCompletados(totalCompletados);
          setModulosEnProgreso(totalProgreso);
        }

        setLoading(false);
      } catch (err) {
        console.log(err);
        setLoading(false);
      }
    };

    if (userName) {
      obtenerDatos();
    }
  }, [userName]);

  return (
    <ContenedorPageTemplate fondo={true}>
      <div className="absolute inset-0 pointer-events-none z-0">
        <SubtleFloatingParticles count={60} />
      </div>

      {/* Header */}
      <div className="relative z-10 mb-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Trophy className="w-10 h-10 md:w-12 md:h-12 text-theme-yellow animate-bounce" />
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-theme-blue via-blue-600 to-theme-yellow bg-clip-text text-transparent">
            Mis Logros
          </h1>
          <Award className="w-10 h-10 md:w-12 md:h-12 text-theme-blue animate-bounce" style={{ animationDelay: '0.2s' }} />
        </div>
        <p className="text-center text-gray-600 text-lg">
          Revisa tu progreso y celebra tus logros académicos
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-theme-blue"></div>
        </div>
      ) : (
        <div className="relative z-10 space-y-8">
          {/* Estadísticas principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto px-4">
            <StatsCard
              icon={Globe}
              label="Total de Idiomas"
              value={idiomasEstudiante?.length || 0}
              delay={200}
              color="blue"
            />
            <StatsCard
              icon={GraduationCap}
              label="Módulos Completados"
              value={modulosCompletados}
              delay={400}
              color="yellow"
            />
            <StatsCard
              icon={TrendingUp}
              label="Módulos en Progreso"
              value={modulosEnProgreso}
              delay={600}
              color="blue"
            />
          </div>

          {/* Detalles de progreso */}
          <div className="max-w-5xl mx-auto px-4">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-xl p-6 md:p-8 border-2 border-white/50">
              <div className="flex items-center gap-3 mb-6">
                <Target className="w-6 h-6 text-theme-blue" />
                <h2 className="text-2xl font-bold text-theme-blue">Resumen de Progreso</h2>
              </div>
              
              <div className="space-y-4">
                {idiomasEstudiante && idiomasEstudiante.length > 0 ? (
                  idiomasEstudiante.map((idioma, index) => (
                    <div 
                      key={index} 
                      className="p-4 bg-gradient-to-r from-blue-50 to-yellow-50 rounded-xl border border-blue-100 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-3xl">
                          {idioma.nombre?.toLowerCase().includes('inglés') ? '🇬🇧' :
                           idioma.nombre?.toLowerCase().includes('francés') ? '🇫🇷' :
                           idioma.nombre?.toLowerCase().includes('español') ? '🇪🇸' :
                           idioma.nombre?.toLowerCase().includes('portugués') ? '🇵🇹' : '🌐'}
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-theme-blue capitalize">{idioma.nombre}</h3>
                          <p className="text-sm text-gray-600">Sigla: {idioma.sigla}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-4">No tienes idiomas asignados</p>
                )}
              </div>
            </div>
          </div>

          {/* Mensaje motivacional */}
          <div className="max-w-3xl mx-auto px-4 text-center">
            <div className="bg-gradient-to-r from-theme-blue to-blue-600 rounded-2xl p-6 text-white shadow-xl">
              <Award className="w-12 h-12 mx-auto mb-3 animate-pulse" />
              <p className="text-xl font-semibold mb-2">
                ¡Sigue así! 🎉
              </p>
              <p className="text-white/90">
                Cada módulo completado es un paso más cerca de tu meta. ¡Continúa aprendiendo!
              </p>
            </div>
          </div>
        </div>
      )}
    </ContenedorPageTemplate>
  );
};

export default MisLogrosPage;






